/**
 * Communication Adapters - Sprint 31
 *
 * Factory and exports for canister communication adapters.
 *
 * Usage:
 * ```typescript
 * import { getAdapter, initializeAdapter } from './api/communication';
 *
 * await initializeAdapter();
 * const result = await getAdapter().call({
 *     canisterName: 'vector_db',
 *     methodName: 'get_all_devices',
 *     args: []
 * });
 * ```
 */

// Re-export types
export * from './types';

// Re-export adapters
export { ProxyAdapter, createProxyAdapter } from './proxy-adapter';
export { AgentAdapter, createAgentAdapter } from './agent-adapter';

import { CommunicationAdapter } from './types';
import { ProxyAdapter } from './proxy-adapter';
import { AgentAdapter } from './agent-adapter';
import { environment, useProxy } from '../../config/environment';

// ============================================================================
// Singleton Adapter Instance
// ============================================================================

/**
 * Singleton adapter instance
 */
let adapter: CommunicationAdapter | null = null;

/**
 * Whether the adapter has been initialized
 */
let isInitialized = false;

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create the appropriate adapter based on environment
 */
function createAdapter(): CommunicationAdapter {
    if (useProxy()) {
        console.log('📡 Creating ProxyAdapter for local development');
        return new ProxyAdapter();
    } else {
        console.log('📡 Creating AgentAdapter for mainnet');
        return new AgentAdapter();
    }
}

/**
 * Get the current adapter instance
 * Creates one if it doesn't exist
 */
export function getAdapter(): CommunicationAdapter {
    if (!adapter) {
        adapter = createAdapter();
    }
    return adapter;
}

/**
 * Initialize the adapter
 * Should be called early in app startup
 */
export async function initializeAdapter(): Promise<CommunicationAdapter> {
    if (isInitialized) {
        return getAdapter();
    }

    const currentAdapter = getAdapter();
    await currentAdapter.initialize();
    isInitialized = true;

    console.log(`✅ Communication adapter ready: ${currentAdapter.name}`);
    return currentAdapter;
}

/**
 * Check if adapter is initialized and ready
 */
export function isAdapterReady(): boolean {
    return isInitialized && adapter !== null && adapter.isReady();
}

/**
 * Reset the adapter (useful for testing)
 */
export function resetAdapter(): void {
    adapter = null;
    isInitialized = false;
}

/**
 * Force a specific adapter type (useful for testing)
 */
export function setAdapter(newAdapter: CommunicationAdapter): void {
    adapter = newAdapter;
    isInitialized = adapter.isReady();
}

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Make a canister call using the current adapter
 * Automatically initializes if needed
 */
export async function callCanister<T = any>(
    canisterName: string,
    methodName: string,
    args: any[] = [],
    options: { isQuery?: boolean; timeout?: number } = {}
): Promise<T> {
    if (!isAdapterReady()) {
        await initializeAdapter();
    }

    return getAdapter().call<T>({
        canisterName,
        methodName,
        args,
        isQuery: options.isQuery,
        timeout: options.timeout
    });
}

/**
 * Make a query call (read-only)
 */
export async function queryCanister<T = any>(
    canisterName: string,
    methodName: string,
    args: any[] = [],
    timeout?: number
): Promise<T> {
    if (!isAdapterReady()) {
        await initializeAdapter();
    }

    return getAdapter().query<T>({
        canisterName,
        methodName,
        args,
        timeout
    });
}

/**
 * Make an update call (modifies state)
 */
export async function updateCanister<T = any>(
    canisterName: string,
    methodName: string,
    args: any[] = [],
    timeout?: number
): Promise<T> {
    if (!isAdapterReady()) {
        await initializeAdapter();
    }

    return getAdapter().update<T>({
        canisterName,
        methodName,
        args,
        timeout
    });
}

// ============================================================================
// Debug Utilities
// ============================================================================

/**
 * Get debug information about current adapter
 */
export function getAdapterInfo(): {
    type: string;
    environment: string;
    ready: boolean;
    host: string;
} {
    return {
        type: adapter?.name || 'not created',
        environment: environment.mode,
        ready: isAdapterReady(),
        host: environment.icHost
    };
}

// Expose on window for debugging
if (typeof window !== 'undefined') {
    (window as any).asdCommunication = {
        getAdapter,
        initializeAdapter,
        isReady: isAdapterReady,
        getInfo: getAdapterInfo,
        call: callCanister,
        query: queryCanister,
        update: updateCanister,
        reset: resetAdapter
    };
}
